import { useEffect, useRef, useState } from "react";
import Tesseract from "tesseract.js";
import { Camera, ScanLine, Sparkles, Check, X, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const ISO_RE = /[A-Z]{4}\s?\d{7}/;

export function OcrScanner({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSnapshot(null); setCandidate(null); setError(null);
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("No se pudo acceder a la cámara. Puede subir una foto del código."));
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);

  const captureFrame = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement("canvas");
    const maxW = 1280;
    const scale = Math.min(1, maxW / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  };

  const extractIso = (text) => {
    const clean = (text || "").toUpperCase().replace(/[^A-Z0-9\s]/g, " ");
    const m = clean.match(ISO_RE);
    return m ? m[0].replace(/\s/g, "") : null;
  };

  const runLocalOcr = async () => {
    const frame = snapshot || captureFrame();
    if (!frame) return;
    setSnapshot(frame);
    setBusy(true); setBusyLabel("Leyendo con OCR local..."); setError(null);
    try {
      const { data } = await Tesseract.recognize(frame, "eng");
      const iso = extractIso(data.text);
      if (iso) setCandidate(iso);
      else setError("No se detectó un código ISO. Intente acercar la cámara o use 'Verificar con IA'.");
    } catch (e) {
      setError("Error al procesar la imagen localmente.");
    } finally {
      setBusy(false);
    }
  };

  const runAiOcr = async () => {
    const frame = snapshot || captureFrame();
    if (!frame) return;
    setSnapshot(frame);
    setBusy(true); setBusyLabel("Analizando con IA en la nube..."); setError(null);
    try {
      const { data } = await api.post("/ocr", { image_base64: frame });
      if (data.container_number) setCandidate(data.container_number);
      else setError("La IA no detectó un código de contenedor en la imagen.");
    } catch (e) {
      setError("Error al consultar el OCR con IA.");
    } finally {
      setBusy(false);
    }
  };

  const retake = () => { setSnapshot(null); setCandidate(null); setError(null); };

  const apply = () => {
    if (candidate) { onResult(candidate); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-navy text-white border-navy-700" data-testid="ocr-scanner-dialog">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="font-outfit text-white flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-telemetry" /> Escáner OCR de Contenedor
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]" data-testid="ocr-camera-view">
            {snapshot ? (
              <img src={snapshot} alt="Captura OCR" className="w-full h-full object-cover" />
            ) : (
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            )}
            {!snapshot && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-x-8 top-1/4 bottom-1/4 border-2 border-telemetry/70 rounded-md" />
                <div className="scan-line absolute inset-x-8 h-0.5 bg-telemetry shadow-[0_0_12px_#00F0FF]" />
              </div>
            )}
          </div>

          {error && <p className="text-sm text-amber-300" data-testid="ocr-error-message">{error}</p>}

          {candidate && (
            <div className="flex items-center justify-between bg-white/10 rounded-lg px-4 py-3 border border-telemetry/30" data-testid="ocr-candidate-box">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">Código detectado</span>
                <p className="font-mono font-bold text-xl tracking-widest text-telemetry" data-testid="ocr-candidate-value">{candidate}</p>
              </div>
              <Check className="w-6 h-6 text-rampgreen" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button onClick={snapshot ? retake : runLocalOcr} disabled={busy} variant="outline"
                    data-testid="ocr-capture-button"
                    className="border-white/30 text-white hover:bg-white/10 bg-transparent">
              {busy && busyLabel.includes("local") ? <RefreshCw className="w-4 h-4 animate-spin" /> : snapshot ? <X className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              {snapshot ? "Nueva captura" : "Capturar y leer"}
            </Button>
            <Button onClick={runAiOcr} disabled={busy}
                    data-testid="ocr-ai-button"
                    className="bg-telemetry text-navy hover:bg-cyan-300 font-semibold">
              {busy && busyLabel.includes("IA") ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Verificar con IA
            </Button>
          </div>
          {busy && <p className="text-xs text-slate-400 text-center" data-testid="ocr-busy-label">{busyLabel}</p>}
          {candidate && (
            <Button onClick={apply} data-testid="ocr-apply-button" className="w-full bg-rampgreen text-navy hover:bg-emerald-400 font-bold">
              Usar este número
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
